import { useState } from 'react';
import { useAuthStore } from '../../features/auth/store';
import { getOspreyApiBaseUrl } from '../utils/apiBase';

export interface UploadedMedia {
  id: string;
  url: string;
}

/**
 * Optional upload targeting. Product/slider media (the default) omits this and
 * lands in the merchant public bucket keyed by the caller's selected tenant.
 * Brand/platform assets pass `tenantType: 'APP'` so media-service routes them to
 * the platform public bucket (GCS_APP_BUCKET, e.g. tcs-platform-media), and an
 * explicit `tenantId` so a superadmin uploading FOR a merchant isn't bound to
 * whatever tenant happens to be selected in the auth store.
 */
export interface UploadOptions {
  tenantId?: string;
  tenantType?: 'MERCHANT' | 'APP';
}

// Media upload goes through osprey-api (which proxies multipart to rab/media). The
// Osprey UI talks ONLY to osprey-api — single origin. See apiBase.ts.
const getBaseUrl = getOspreyApiBaseUrl;

export function useMediaUpload() {
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const upload = async (
    file: File,
    solutionId = 'rack-and-box',
    opts?: UploadOptions,
  ): Promise<UploadedMedia> => {
    setIsUploading(true);
    setError(null);

    const { token, selectedTenantId } = useAuthStore.getState();
    const form = new FormData();
    form.append('file', file);

    const headers: Record<string, string> = {
      Accept: 'application/json',
    };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    // Explicit target tenant (brand assets uploaded FOR a merchant) wins over the
    // ambient selected tenant. media-service requires x-tenant-id regardless of
    // tenant-type — APP assets are still isolated under a per-tenant key prefix.
    const tenantId = opts?.tenantId ?? selectedTenantId;
    if (tenantId) {
      headers['x-tenant-id'] = tenantId;
    }
    if (opts?.tenantType) {
      headers['x-tenant-type'] = opts.tenantType;
    }
    headers['x-solution-id'] = solutionId;

    try {
      const res = await fetch(`${getBaseUrl()}/media/upload`, {
        method: 'POST',
        headers,
        body: form,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error((err as { message?: string })?.message ?? `Upload failed: ${res.status}`);
      }
      return res.json() as Promise<UploadedMedia>;
    } catch (e: unknown) {
      const msg = (e as Error).message ?? 'Upload failed';
      setError(msg);
      throw e;
    } finally {
      setIsUploading(false);
    }
  };

  return { upload, isUploading, error };
}
